/**
 * Table maths (#338): Excel-like formulas in editor tables.
 *
 * A cell whose text starts with `=` is a formula. This module evaluates every
 * formula in one table and reports a number or an error code per cell. It is
 * pure (no ProseMirror, no DOM) so the editor's decoration plugin and the
 * Markdown export share it.
 *
 * Safety: formulas are user text, so nothing here ever reaches eval or
 * Function(). A small tokenizer and recursive-descent parser build an AST;
 * parser nesting, reference-chain depth and total evaluation work are all
 * capped, and a cap that trips shows `#LIMIT!` instead of freezing the tab.
 *
 * References are A1-style over the table's grid: column letters from A,
 * 1-based rows counting the header row. A merged cell lives at its top-left
 * grid position; the other positions it covers read as empty.
 */

export interface CellInput {
  /** The cell's plain text. */
  text: string;
  /** A header cell (th). ABOVE/LEFT totals skip these. */
  header: boolean;
  colspan?: number;
  rowspan?: number;
}

/** Rows of cells, in document order (a merged cell appears once). */
export type TableInput = CellInput[][];

export type ErrorCode = '#DIV/0!' | '#REF!' | '#CYCLE!' | '#NAME?' | '#VALUE!' | '#ERROR!' | '#LIMIT!';

export type CellResult =
  | { kind: 'number'; value: number }
  | { kind: 'error'; code: ErrorCode };

export const TOTAL_FUNCTIONS = [
  { fn: 'SUM', label: 'Sum' },
  { fn: 'AVERAGE', label: 'Average' },
  { fn: 'COUNT', label: 'Count' },
  { fn: 'MIN', label: 'Min' },
  { fn: 'MAX', label: 'Max' },
] as const;

export type TotalFn = (typeof TOTAL_FUNCTIONS)[number]['fn'];
export type TotalDirection = 'ABOVE' | 'LEFT';

/** The formula a total cell holds, e.g. `=SUM(ABOVE)`. */
export function totalFormula(fn: TotalFn, direction: TotalDirection): string {
  return `=${fn}(${direction})`;
}

/** If `text` is a total formula (`=FN(ABOVE)` / `=FN(LEFT)`), its parts. */
export function totalFunctionOf(text: string): { fn: TotalFn; direction: TotalDirection } | null {
  const m = /^\s*=\s*([A-Za-z]+)\s*\(\s*(ABOVE|LEFT)\s*\)\s*$/i.exec(text);
  if (!m) return null;
  const fn = m[1].toUpperCase();
  const known = TOTAL_FUNCTIONS.find((f) => f.fn === fn);
  if (!known) return null;
  return { fn: known.fn, direction: m[2].toUpperCase() as TotalDirection };
}

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

/** Longest formula we will tokenize. */
const MAX_FORMULA_LENGTH = 20_000;
/** Parser nesting (parentheses, unary signs, function calls). */
const MAX_PARSE_DEPTH = 64;
/** Formula -> formula reference chain depth during evaluation. */
const MAX_REF_DEPTH = 200;
/** Total work units (cell reads and AST nodes) for one table. */
const MAX_STEPS = 200_000;
/** Largest grid we will lay out. */
const MAX_GRID_CELLS = 100_000;
/** colspan/rowspan are clamped to this. */
const MAX_SPAN = 1000;
/** Text longer than this is never read as a number. */
const MAX_NUMBER_TEXT = 100;

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

const NUMBER_RE = /^(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?$/;
// $ euro pound yen, written as escapes to keep the source ASCII.
const CURRENCY_RE = /[$\u20AC\u00A3\u00A5]/g;

/**
 * Read a cell's text as a number, leniently: currency symbols, thousands
 * separators, spaces, a trailing percent and accounting brackets are allowed.
 * Anything else is not a number (null).
 */
export function parseNumber(text: string): number | null {
  if (text.length > MAX_NUMBER_TEXT) return null;
  let s = text.replace(/[\s\u00A0,]/g, '');
  if (s === '') return null;
  let negative = false;
  if (s.startsWith('(') && s.endsWith(')')) {
    negative = true;
    s = s.slice(1, -1);
  }
  let percent = false;
  if (s.endsWith('%')) {
    percent = true;
    s = s.slice(0, -1);
  }
  // Signs may sit either side of a currency symbol: -$5 and $-5.
  let sawCurrency = false;
  s = s.replace(CURRENCY_RE, () => {
    sawCurrency = true;
    return '';
  });
  if (sawCurrency && /[$\u20AC\u00A3\u00A5]/.test(s)) return null;
  if (s.startsWith('-') || s.startsWith('+')) {
    if (s.startsWith('-')) negative = !negative;
    s = s.slice(1);
  }
  if (!NUMBER_RE.test(s)) return null;
  let n = Number(s);
  if (!Number.isFinite(n)) return null;
  if (percent) n /= 100;
  return negative ? -n : n;
}

/** A formula is text starting with `=` with something after it. */
export function isFormula(text: string): boolean {
  const t = text.trim();
  return t.length > 1 && t.startsWith('=');
}

/** 0 -> A, 25 -> Z, 26 -> AA. */
export function columnLabel(index: number): string {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

function columnIndex(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/** Display a result: numbers with thousands separators and no float noise. */
export function formatResult(result: CellResult): string {
  if (result.kind === 'error') return result.code;
  const v = Number.parseFloat(result.value.toPrecision(12));
  if (Object.is(v, -0) || v === 0) return '0';
  return v.toLocaleString('en-US', { maximumFractionDigits: 10 });
}

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type Token =
  | { t: 'num'; v: number; raw: string }
  | { t: 'ident'; v: string }
  | { t: 'op'; v: '+' | '-' | '*' | '/' | '(' | ')' | ',' | ':' | '%' };

class FormulaError extends Error {
  readonly code: ErrorCode;
  constructor(code: ErrorCode) {
    super(code);
    this.code = code;
  }
}

const NUM_TOKEN_RE = /(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/y;
const IDENT_TOKEN_RE = /[A-Za-z_][A-Za-z0-9_]*/y;

function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\u00A0') {
      i++;
      continue;
    }
    if ('+-*/(),:%'.includes(ch)) {
      tokens.push({ t: 'op', v: ch as '+' });
      i++;
      continue;
    }
    if ((ch >= '0' && ch <= '9') || ch === '.') {
      NUM_TOKEN_RE.lastIndex = i;
      const m = NUM_TOKEN_RE.exec(src);
      if (!m) throw new FormulaError('#ERROR!');
      const n = Number(m[0]);
      if (!Number.isFinite(n)) throw new FormulaError('#VALUE!');
      tokens.push({ t: 'num', v: n, raw: m[0] });
      i += m[0].length;
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      IDENT_TOKEN_RE.lastIndex = i;
      const m = IDENT_TOKEN_RE.exec(src);
      if (!m || m[0].length > 64) throw new FormulaError('#ERROR!');
      tokens.push({ t: 'ident', v: m[0] });
      i += m[0].length;
      continue;
    }
    throw new FormulaError('#ERROR!');
  }
  return tokens;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

/** A position or a whole column/row; row/col are 0-based, -1 = unbounded. */
interface RefPart {
  row: number;
  col: number;
}

type Node =
  | { k: 'num'; v: number }
  | { k: 'ref'; at: RefPart }
  | { k: 'range'; from: RefPart; to: RefPart }
  | { k: 'keyword'; name: 'ABOVE' | 'LEFT' }
  | { k: 'name'; name: string }
  | { k: 'call'; name: string; args: Node[] }
  | { k: 'neg'; a: Node }
  | { k: 'pct'; a: Node }
  // Flat chains keep long `1+1+1...` formulas from nesting deeply.
  | { k: 'chain'; first: Node; rest: Array<{ op: '+' | '-' | '*' | '/'; node: Node }> };

const CELL_RE = /^([A-Za-z]{1,3})(\d+)$/;
const COL_RE = /^[A-Za-z]{1,3}$/;

class Parser {
  private pos = 0;
  private depth = 0;
  private readonly tokens: Token[];
  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  parse(): Node {
    const node = this.additive();
    if (this.pos !== this.tokens.length) throw new FormulaError('#ERROR!');
    return node;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private isOp(v: string): boolean {
    const tok = this.peek();
    return tok !== undefined && tok.t === 'op' && tok.v === v;
  }

  private expectOp(v: string): void {
    if (!this.isOp(v)) throw new FormulaError('#ERROR!');
    this.pos++;
  }

  private enter(): void {
    if (++this.depth > MAX_PARSE_DEPTH) throw new FormulaError('#LIMIT!');
  }

  private additive(): Node {
    const first = this.multiplicative();
    const rest: Array<{ op: '+' | '-'; node: Node }> = [];
    while (this.isOp('+') || this.isOp('-')) {
      const op = (this.tokens[this.pos++] as { v: '+' | '-' }).v;
      rest.push({ op, node: this.multiplicative() });
    }
    return rest.length ? { k: 'chain', first, rest } : first;
  }

  private multiplicative(): Node {
    const first = this.unary();
    const rest: Array<{ op: '*' | '/'; node: Node }> = [];
    while (this.isOp('*') || this.isOp('/')) {
      const op = (this.tokens[this.pos++] as { v: '*' | '/' }).v;
      rest.push({ op, node: this.unary() });
    }
    return rest.length ? { k: 'chain', first, rest } : first;
  }

  private unary(): Node {
    if (this.isOp('-') || this.isOp('+')) {
      const negate = this.isOp('-');
      this.pos++;
      this.enter();
      const a = this.unary();
      this.depth--;
      return negate ? { k: 'neg', a } : a;
    }
    let node = this.primary();
    while (this.isOp('%')) {
      this.pos++;
      node = { k: 'pct', a: node };
    }
    return node;
  }

  private primary(): Node {
    const tok = this.peek();
    if (!tok) throw new FormulaError('#ERROR!');

    if (tok.t === 'op' && tok.v === '(') {
      this.pos++;
      this.enter();
      const inner = this.additive();
      this.depth--;
      this.expectOp(')');
      return inner;
    }

    if (tok.t === 'num') {
      this.pos++;
      if (this.isOp(':')) {
        // Whole-row range, 2:5.
        this.pos++;
        const end = this.peek();
        if (!end || end.t !== 'num' || !/^\d+$/.test(tok.raw) || !/^\d+$/.test(end.raw)) {
          throw new FormulaError('#ERROR!');
        }
        this.pos++;
        return { k: 'range', from: { row: tok.v - 1, col: -1 }, to: { row: end.v - 1, col: -1 } };
      }
      return { k: 'num', v: tok.v };
    }

    if (tok.t === 'ident') {
      this.pos++;
      if (this.isOp('(')) {
        this.pos++;
        this.enter();
        const args: Node[] = [];
        if (!this.isOp(')')) {
          args.push(this.additive());
          while (this.isOp(',')) {
            this.pos++;
            args.push(this.additive());
          }
        }
        this.depth--;
        this.expectOp(')');
        return { k: 'call', name: tok.v.toUpperCase(), args };
      }
      if (this.isOp(':')) {
        this.pos++;
        const end = this.peek();
        if (!end || end.t !== 'ident') throw new FormulaError('#ERROR!');
        this.pos++;
        const a = CELL_RE.exec(tok.v);
        const b = CELL_RE.exec(end.v);
        if (a && b) {
          return { k: 'range', from: cellPart(a), to: cellPart(b) };
        }
        if (COL_RE.test(tok.v) && COL_RE.test(end.v)) {
          return {
            k: 'range',
            from: { row: -1, col: columnIndex(tok.v) },
            to: { row: -1, col: columnIndex(end.v) },
          };
        }
        throw new FormulaError('#ERROR!');
      }
      const cell = CELL_RE.exec(tok.v);
      if (cell) return { k: 'ref', at: cellPart(cell) };
      const upper = tok.v.toUpperCase();
      if (upper === 'ABOVE' || upper === 'LEFT') return { k: 'keyword', name: upper };
      return { k: 'name', name: tok.v };
    }

    throw new FormulaError('#ERROR!');
  }
}

function cellPart(m: RegExpExecArray): RefPart {
  // Row "0" stays -1 after the shift, and is reported as #REF! at evaluation.
  const row = Number(m[2]);
  return { row: row >= 1 ? row - 1 : -2, col: columnIndex(m[1]) };
}

/** Parse a formula (with its leading `=`). Throws FormulaError. */
function parseFormula(text: string): Node {
  const body = text.trim().slice(1);
  if (body.length > MAX_FORMULA_LENGTH) throw new FormulaError('#LIMIT!');
  return new Parser(tokenize(body)).parse();
}

// ---------------------------------------------------------------------------
// Grid
// ---------------------------------------------------------------------------

interface Anchor {
  row: number;
  col: number;
  /** Index into the document's row/cell arrays. */
  docRow: number;
  docCell: number;
  text: string;
  header: boolean;
}

interface Grid {
  rows: number;
  cols: number;
  /** anchors[r][c] is the cell whose top-left is (r, c), else null. */
  anchors: (Anchor | null)[][];
  list: Anchor[];
}

function clampSpan(n: number | undefined): number {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 1) return 1;
  return Math.min(Math.floor(n), MAX_SPAN);
}

/** Lay a table out on a grid, honouring colspan/rowspan. Null when too big. */
function buildGrid(input: TableInput): Grid | null {
  const rows = input.length;
  let widthBound = 0;
  for (const row of input) {
    let w = 0;
    for (const cell of row) w += clampSpan(cell.colspan);
    widthBound = Math.max(widthBound, w);
  }
  // Rowspans from above can push a row wider than its own colspans.
  const bound = widthBound * 2;
  if (rows * bound > MAX_GRID_CELLS) return null;

  const occupied: boolean[][] = Array.from({ length: rows }, () => new Array<boolean>(bound).fill(false));
  const anchors: (Anchor | null)[][] = Array.from({ length: rows }, () => new Array<Anchor | null>(bound).fill(null));
  const list: Anchor[] = [];
  let cols = 0;

  for (let r = 0; r < rows; r++) {
    let c = 0;
    input[r].forEach((cell, i) => {
      while (c < bound && occupied[r][c]) c++;
      const cs = clampSpan(cell.colspan);
      const rs = clampSpan(cell.rowspan);
      if (c + cs > bound) return; // cannot place (malformed); treated as absent
      const anchor: Anchor = { row: r, col: c, docRow: r, docCell: i, text: cell.text, header: cell.header };
      anchors[r][c] = anchor;
      list.push(anchor);
      for (let rr = r; rr < Math.min(rows, r + rs); rr++) {
        for (let cc = c; cc < c + cs; cc++) occupied[rr][cc] = true;
      }
      c += cs;
      cols = Math.max(cols, c);
    });
  }
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < bound; c++) if (occupied[r][c]) cols = Math.max(cols, c + 1);
  }
  for (const row of anchors) row.length = cols;
  return { rows, cols, anchors, list };
}

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

type Value =
  | { t: 'num'; v: number }
  | { t: 'str' }
  | { t: 'empty' }
  | { t: 'err'; code: ErrorCode };

const EMPTY: Value = { t: 'empty' };

function err(code: ErrorCode): Value {
  return { t: 'err', code };
}

/** What a function argument yields: a list of cell values, or one scalar. */
type ArgValues = { cells: Value[] } | { scalar: Value };

class Evaluator {
  private steps = 0;
  private refDepth = 0;
  private memo = new Map<Anchor, Value>();
  private inProgress = new Set<Anchor>();
  private asts = new Map<Anchor, Node | FormulaError>();

  private readonly grid: Grid;
  constructor(grid: Grid) {
    this.grid = grid;
  }

  private tick(n = 1): void {
    this.steps += n;
    if (this.steps > MAX_STEPS) throw new FormulaError('#LIMIT!');
  }

  /** Value of the cell at a grid position (covered positions are empty). */
  private valueAt(row: number, col: number): Value {
    this.tick();
    const anchor = this.grid.anchors[row]?.[col] ?? null;
    if (!anchor) return EMPTY;
    return this.cellValue(anchor);
  }

  cellValue(anchor: Anchor): Value {
    const cached = this.memo.get(anchor);
    if (cached) return cached;
    if (!isFormula(anchor.text)) {
      const text = anchor.text.trim();
      if (text === '') return EMPTY;
      const n = parseNumber(text);
      return n === null ? { t: 'str' } : { t: 'num', v: n };
    }
    if (this.inProgress.has(anchor)) return err('#CYCLE!');
    if (this.refDepth >= MAX_REF_DEPTH) throw new FormulaError('#LIMIT!');

    this.inProgress.add(anchor);
    this.refDepth++;
    let result: Value;
    try {
      result = this.evaluateFormula(anchor);
    } catch (e) {
      // A cap tripping deep in the chain must unwind every frame above it;
      // only the outermost formula records the result.
      this.refDepth--;
      this.inProgress.delete(anchor);
      if (e instanceof FormulaError && e.code === '#LIMIT!' && this.refDepth > 0) throw e;
      result = err(e instanceof FormulaError ? e.code : '#VALUE!');
      this.memo.set(anchor, result);
      return result;
    }
    this.refDepth--;
    this.inProgress.delete(anchor);
    this.memo.set(anchor, result);
    return result;
  }

  private evaluateFormula(anchor: Anchor): Value {
    let ast = this.asts.get(anchor);
    if (!ast) {
      try {
        ast = parseFormula(anchor.text);
      } catch (e) {
        ast = e instanceof FormulaError ? e : new FormulaError('#ERROR!');
      }
      this.asts.set(anchor, ast);
    }
    if (ast instanceof FormulaError) return err(ast.code);
    const v = this.scalar(ast, anchor);
    if (v.t === 'str') return err('#VALUE!');
    if (v.t === 'empty') return { t: 'num', v: 0 };
    return v;
  }

  private checkPart(part: RefPart): void {
    if (part.row === -2) throw new FormulaError('#REF!');
    if (part.row >= this.grid.rows || part.col >= this.grid.cols) throw new FormulaError('#REF!');
  }

  /** Every value a range or keyword covers, minus the formula's own cell. */
  private collect(node: Node, self: Anchor): Value[] | null {
    const { rows, cols } = this.grid;
    let r1: number, r2: number, c1: number, c2: number;
    let skipHeaders = false;
    if (node.k === 'range') {
      this.checkPart(node.from);
      this.checkPart(node.to);
      // A whole-column range spans every row; a whole-row range every column.
      const wholeColumn = node.from.row === -1;
      const wholeRow = node.from.col === -1;
      r1 = wholeColumn ? 0 : Math.min(node.from.row, node.to.row);
      r2 = wholeColumn ? rows - 1 : Math.max(node.from.row, node.to.row);
      c1 = wholeRow ? 0 : Math.min(node.from.col, node.to.col);
      c2 = wholeRow ? cols - 1 : Math.max(node.from.col, node.to.col);
      if ((wholeColumn && cols === 0) || (wholeRow && rows === 0)) throw new FormulaError('#REF!');
    } else if (node.k === 'keyword') {
      skipHeaders = true;
      if (node.name === 'ABOVE') {
        r1 = 0;
        r2 = self.row - 1;
        c1 = c2 = self.col;
      } else {
        c1 = 0;
        c2 = self.col - 1;
        r1 = r2 = self.row;
      }
    } else if (node.k === 'ref') {
      this.checkPart(node.at);
      r1 = r2 = node.at.row;
      c1 = c2 = node.at.col;
      // A single reference is read even if it is the formula itself (a cycle).
      return [this.valueAt(r1, c1)];
    } else {
      return null;
    }

    const out: Value[] = [];
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        const anchor = this.grid.anchors[r]?.[c] ?? null;
        if (anchor === self) continue;
        if (skipHeaders && anchor?.header) continue;
        out.push(this.valueAt(r, c));
      }
    }
    return out;
  }

  private scalar(node: Node, self: Anchor): Value {
    this.tick();
    switch (node.k) {
      case 'num':
        return { t: 'num', v: node.v };
      case 'ref': {
        this.checkPart(node.at);
        return this.valueAt(node.at.row, node.at.col);
      }
      case 'range':
      case 'keyword':
        return err('#VALUE!');
      case 'name':
        return err('#NAME?');
      case 'neg': {
        const a = this.number(node.a, self);
        return a.t === 'num' ? { t: 'num', v: -a.v } : a;
      }
      case 'pct': {
        const a = this.number(node.a, self);
        return a.t === 'num' ? { t: 'num', v: a.v / 100 } : a;
      }
      case 'chain': {
        let acc = this.number(node.first, self);
        if (acc.t === 'err') return acc;
        for (const { op, node: rhs } of node.rest) {
          const b = this.number(rhs, self);
          if (b.t === 'err') return b;
          if (acc.t !== 'num' || b.t !== 'num') return err('#VALUE!');
          let v: number;
          if (op === '+') v = acc.v + b.v;
          else if (op === '-') v = acc.v - b.v;
          else if (op === '*') v = acc.v * b.v;
          else {
            if (b.v === 0) return err('#DIV/0!');
            v = acc.v / b.v;
          }
          if (!Number.isFinite(v)) return err('#VALUE!');
          acc = { t: 'num', v };
        }
        return acc;
      }
      case 'call':
        return this.call(node.name, node.args, self);
    }
  }

  /** A scalar coerced for arithmetic: empty is 0, text is #VALUE!. */
  private number(node: Node, self: Anchor): Value {
    const v = this.scalar(node, self);
    if (v.t === 'empty') return { t: 'num', v: 0 };
    if (v.t === 'str') return err('#VALUE!');
    if (v.t === 'num' && !Number.isFinite(v.v)) return err('#VALUE!');
    return v;
  }

  private call(name: string, args: Node[], self: Anchor): Value {
    if (!FUNCTIONS.has(name)) return err('#NAME?');
    if (args.length === 0) return err('#VALUE!');

    const values: ArgValues[] = args.map((arg) => {
      const cells = this.collect(arg, self);
      return cells ? { cells } : { scalar: this.scalar(arg, self) };
    });

    const nums: number[] = [];
    let nonEmpty = 0;
    let firstError: Value | null = null;
    for (const a of values) {
      if ('cells' in a) {
        for (const v of a.cells) {
          if (v.t !== 'empty') nonEmpty++;
          if (v.t === 'num') nums.push(v.v);
          else if (v.t === 'err' && !firstError) firstError = v;
        }
      } else {
        const v = a.scalar;
        if (v.t !== 'empty') nonEmpty++;
        if (v.t === 'num') nums.push(v.v);
        else if (v.t === 'err' && !firstError) firstError = v;
      }
    }

    if (name === 'COUNT') return { t: 'num', v: nums.length };
    if (name === 'COUNTA') return { t: 'num', v: nonEmpty };
    if (firstError) return firstError;

    let v: number;
    switch (name) {
      case 'SUM':
        v = nums.reduce((s, n) => s + n, 0);
        break;
      case 'AVERAGE':
        if (nums.length === 0) return err('#DIV/0!');
        v = nums.reduce((s, n) => s + n, 0) / nums.length;
        break;
      case 'MIN':
        v = nums.length ? nums.reduce((m, n) => (n < m ? n : m), Infinity) : 0;
        break;
      case 'MAX':
        v = nums.length ? nums.reduce((m, n) => (n > m ? n : m), -Infinity) : 0;
        break;
      default:
        return err('#NAME?');
    }
    return Number.isFinite(v) ? { t: 'num', v } : err('#VALUE!');
  }
}

const FUNCTIONS = new Set(['SUM', 'AVERAGE', 'COUNT', 'COUNTA', 'MIN', 'MAX']);

/**
 * Evaluate every formula in a table. The result mirrors the input's shape:
 * `result[row][cell]` is the formula's result, or null for a cell that is not
 * a formula. Never throws.
 */
export function evaluateTable(input: TableInput): (CellResult | null)[][] {
  const out: (CellResult | null)[][] = input.map((row) =>
    row.map((cell) => (isFormula(cell.text) ? { kind: 'error', code: '#LIMIT!' } : null)),
  );
  if (!out.some((row) => row.some(Boolean))) return out;

  const grid = buildGrid(input);
  if (!grid) return out;

  const evaluator = new Evaluator(grid);
  // Row-major order keeps reference chains that run down a column shallow.
  for (const anchor of grid.list) {
    if (!isFormula(anchor.text)) continue;
    let v: Value;
    try {
      v = evaluator.cellValue(anchor);
    } catch (e) {
      v = err(e instanceof FormulaError ? e.code : '#VALUE!');
    }
    out[anchor.docRow][anchor.docCell] =
      v.t === 'num' ? { kind: 'number', value: v.v } : v.t === 'err' ? { kind: 'error', code: v.code } : { kind: 'number', value: 0 };
  }
  return out;
}
